import { useEffect, useState } from 'react';
import type { Editor } from '@tiptap/core';
import { Dialog } from '../dialogs/dialog';
import { Button } from 'primereact/button';
import { InputNumber } from 'primereact/inputnumber';
import { Dropdown } from 'primereact/dropdown';
import { Checkbox } from 'primereact/checkbox';
import { CARD_VARIANTS, DEFAULT_CARD_LAYOUT, normalizeCardLayout } from '../../general/card-group-layout';
import type { CardGroupLayout } from '../../general/card-group-layout';
import { cardContext, insertCardGroup } from './card-group';

export function CardGroupDialog({ editor, request }: { editor: Editor; request: number }) {
    const [visible, setVisible] = useState(false);
    const [layout, setLayout] = useState<CardGroupLayout>(DEFAULT_CARD_LAYOUT);
    const [count, setCount] = useState(3);
    const [position, setPosition] = useState<number | null>(null);
    const [error, setError] = useState('');
    useEffect(() => {
        if (!request) return;
        const context = cardContext(editor);
        setPosition(context?.pos ?? null);
        setLayout(normalizeCardLayout(context?.group.attrs));
        setCount(3); setError(''); setVisible(true);
    }, [editor, request]);
    const number = (key: keyof CardGroupLayout, label: string, min: number, max: number) => <div className="field">
        <label htmlFor={`card-${key}`} className="block mb-2">{label}</label>
        <InputNumber inputId={`card-${key}`} value={layout[key] as number} min={min} max={max} useGrouping={false} className="w-full" inputClassName="w-full"
            onValueChange={event => setLayout(current => normalizeCardLayout({ ...current, [key]: event.value ?? DEFAULT_CARD_LAYOUT[key] }))} />
    </div>;
    const apply = () => {
        if (position !== null) {
            const node = editor.state.doc.nodeAt(position);
            if (node?.type.name !== 'cardGroup') { setError('This card group is no longer available. Close the dialog and select it again.'); return; }
            editor.view.dispatch(editor.state.tr.setNodeMarkup(position, undefined, { ...node.attrs, ...normalizeCardLayout(layout) }));
            editor.commands.focus();
        } else if (!insertCardGroup(editor, layout, count)) { setError('The card group could not be inserted at this position. Select a position outside a table or answer field and try again.'); return; }
        setVisible(false);
    };
    return <Dialog visible={visible} onHide={() => setVisible(false)} header={position === null ? 'Insert card group' : 'Card group properties'}
        style={{ width: '36rem', maxWidth: '95vw' }} footer={<>
            <Button label="Cancel" text onClick={() => setVisible(false)} />
            <Button label={position === null ? 'Insert' : 'Apply'} icon="pi pi-check" onClick={apply} />
        </>}>
        <p className="mt-0">Cards keep their reading order and adapt to the available screen width. Each card can contain text, images, and other lesson content.</p>
        {position === null && <div className="field"><label htmlFor="card-count" className="block mb-2">Number of cards</label>
            <InputNumber inputId="card-count" value={count} min={1} max={12} useGrouping={false} className="w-full" inputClassName="w-full" onValueChange={event => setCount(event.value ?? 3)} /></div>}
        <div className="grid">
            <div className="col-12 md:col-6"><h4>Wider screens</h4>
                {number('columns', 'Cards per row', 1, 4)}
                {number('gap', 'Space between cards (px)', 0, 64)}
                {number('padding', 'Space inside cards (px)', 0, 64)}
            </div>
            <div className="col-12 md:col-6"><h4>Mobile (640px and below)</h4>
                {number('mobileColumns', 'Cards per row on mobile', 1, 2)}
                {number('mobileGap', 'Space between cards on mobile (px)', 0, 64)}
                {number('mobilePadding', 'Space inside cards on mobile (px)', 0, 64)}
            </div>
        </div>
        <div className="field"><label htmlFor="card-variant" className="block mb-2">Card style</label>
            <Dropdown inputId="card-variant" className="w-full" value={layout.variant} options={CARD_VARIANTS.map(value => ({ label: value.charAt(0).toUpperCase() + value.slice(1), value }))}
                onChange={event => setLayout(current => ({ ...current, variant: event.value }))} />
        </div>
        <div className="flex align-items-center gap-2"><Checkbox inputId="card-equal-height" checked={layout.equalHeight}
            onChange={event => setLayout(current => ({ ...current, equalHeight: !!event.checked }))} /><label htmlFor="card-equal-height">Equal card heights within each row</label></div>
        {error && <p role="alert">{error}</p>}
    </Dialog>;
}
